import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormInput } from './FormInput';
import type { CompanyOptionalData } from '../types';

type Props = {
  value: CompanyOptionalData;
  onChange: (patch: CompanyOptionalData) => void;
  section: 'overview' | 'practical';
  emailRequired?: boolean;
};

// Practical info fields get a grey example as placeholder: guidance only, never
// saved as a value (same wording as company settings and the admin view).
const PRACTICAL_PLACEHOLDERS = new Set<keyof CompanyOptionalData>([
  'geographicArea', 'hourlyRate', 'responseTime', 'leadTime', 'companyPresentation', 'specialties', 'languages'
]);

export function CompanyOptionalFields({ value, onChange, section, emailRequired }: Props) {
  const { t } = useTranslation();
  const placeholder = (name: keyof CompanyOptionalData) =>
    PRACTICAL_PLACEHOLDERS.has(name) ? t(`companySetup.placeholders.${name}`) : undefined;
  const fields: Array<{ name: keyof CompanyOptionalData; maxLength: number; type?: string }> = section === 'overview'
    ? [{ name: 'email', maxLength: 320, type: 'email' }, { name: 'phone', maxLength: 50, type: 'tel' }, { name: 'website', maxLength: 500 }, { name: 'country', maxLength: 120 }]
    : [{ name: 'geographicArea', maxLength: 255 }, { name: 'hourlyRate', maxLength: 80 }, { name: 'responseTime', maxLength: 80 }, { name: 'leadTime', maxLength: 80 }];

  return <>
    <div className="row">
      {fields.map(({ name, maxLength, type }) => <div className="col-12 col-md-6" key={name}>
        <FormInput name={name} label={t(`companySetup.fields.${name}`)} type={type || 'text'}
          value={String(value[name] ?? '')} maxLength={maxLength} required={name === 'email' && emailRequired}
          placeholder={placeholder(name)}
          onChange={event => onChange({ [name]: event.target.value })} />
      </div>)}
    </div>
    {section === 'practical' && <>
      <div className="mb-4">
        <label className="form-label fw-semibold" htmlFor="companyPresentation">{t('companySetup.fields.companyPresentation')}</label>
        <textarea id="companyPresentation" name="companyPresentation" className="form-control" rows={4} maxLength={20000}
          placeholder={placeholder('companyPresentation')}
          value={value.companyPresentation || ''} onChange={event => onChange({ companyPresentation: event.target.value })} />
      </div>
      <div className="row">
        {(['specialties', 'languages'] as const).map(name => <div className="col-12 col-md-6" key={name}>
          <FormInput name={name} label={t(`companySetup.fields.${name}`)} value={(value[name] || []).join(',')}
            placeholder={placeholder(name)}
            aria-describedby="company-list-help" onChange={event => onChange({ [name]: event.target.value.split(',') })} />
        </div>)}
      </div>
      <p className="form-text" id="company-list-help">{t('companySetup.listHelp')}</p>
      <FormInput name="experienceYears" label={t('companySetup.fields.experienceYears')} type="number" min={0} max={300} step={1}
        value={value.experienceYears ?? 0} onChange={event => onChange({ experienceYears: Number(event.target.value) })} />
    </>}
  </>;
}
